import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../components/auth/AuthContext';
import { api } from '../lib/api';

const AccountContext = createContext();

export const useAccount = () => {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccount must be used within AccountProvider');
  }
  return context;
};

export const AccountProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [activeAccount, setActiveAccount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadAccounts = useCallback(async () => {
    if (!currentUser) {
      setAccounts([]);
      setActiveAccount(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.get('/api/user/accounts');
      const data = response.data?.accounts || response.data || [];
      const activeAccountId = response.data?.active_account_id;
      
      setAccounts(data);
      
      // Encontrar conta ativa
      const active = data.find(acc => acc.id === activeAccountId) || data[0] || null;
      setActiveAccount(active);
      
      // Salvar no localStorage
      if (active) {
        localStorage.setItem('activeAccountId', active.id);
      }
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error('Erro ao carregar contas:', err);
      }
      setAccounts([]);
      setActiveAccount(null);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  const switchAccount = useCallback(async (accountId) => {
    try {
      await api.post(`/api/user/accounts/${accountId}/activate`);
      await loadAccounts();
      
      // Disparar evento para atualizar outros contextos
      window.dispatchEvent(new Event('accountSwitched'));
    } catch (err) {
      console.error('Erro ao alternar conta:', err);
      throw err;
    }
  }, [loadAccounts]);

  const createAccount = useCallback(async (accountData) => {
    try {
      const response = await api.post('/api/user/accounts', accountData);
      await loadAccounts();
      return response.data;
    } catch (err) {
      console.error('Erro ao criar conta:', err);
      throw err;
    }
  }, [loadAccounts]);

  const updateAccount = useCallback(async (accountId, accountData) => {
    try {
      // Se brand_identity estiver presente, mesclar com o existente
      if (accountData.brand_identity) {
        // Buscar conta atual
        const accountResponse = await api.get(`/api/user/accounts/${accountId}`);
        const currentAccount = accountResponse.data;
        
        // Mesclar brand_identity
        const updatedAccount = {
          name: accountData.name !== undefined ? accountData.name : currentAccount.name,
          description: accountData.description !== undefined ? accountData.description : currentAccount.description,
          type: accountData.type !== undefined ? accountData.type : currentAccount.type,
          brand_identity: { ...(currentAccount.brand_identity || {}), ...accountData.brand_identity }
        };
        
        const response = await api.put(`/api/user/accounts/${accountId}`, updatedAccount);
        await loadAccounts();
        return response.data;
      } else {
        // Atualização normal
        const response = await api.put(`/api/user/accounts/${accountId}`, accountData);
        await loadAccounts();
        return response.data;
      }
    } catch (err) {
      console.error('Erro ao atualizar conta:', err);
      throw err;
    }
  }, [loadAccounts]);

  const deleteAccount = useCallback(async (accountId) => {
    try {
      await api.delete(`/api/user/accounts/${accountId}`);
      await loadAccounts();
    } catch (err) {
      console.error('Erro ao deletar conta:', err);
      throw err;
    }
  }, [loadAccounts]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  // Escutar eventos de mudança de conta
  useEffect(() => {
    const handleAccountSwitch = () => {
      loadAccounts();
    };
    
    window.addEventListener('accountSwitched', handleAccountSwitch);
    return () => {
      window.removeEventListener('accountSwitched', handleAccountSwitch);
    };
  }, [loadAccounts]);

  const contextValue = useMemo(() => ({
    accounts,
    activeAccount,
    isLoading,
    loadAccounts,
    switchAccount,
    createAccount,
    updateAccount,
    deleteAccount
  }), [accounts, activeAccount, isLoading, loadAccounts, switchAccount, createAccount, updateAccount, deleteAccount]);

  return (
    <AccountContext.Provider value={contextValue}>
      {children}
    </AccountContext.Provider>
  );
};

